import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { Leaf, TreePine, Flower2, Factory, Sprout, ArrowRight, Globe, Shield, Network } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const AgroVegetalPage = () => {
  const [heroRef, heroInView] = useInView();
  const [branchesRef, branchesInView] = useInView();
  const [engagementsRef, engagementsInView] = useInView();

  const branches = [
    { icon: Factory, title: 'AGRO\'SCOP', subtitle: 'Agro-transformation', desc: 'Transformation des produits agricoles locaux, valorisation des circuits courts et accompagnement des producteurs.', gradient: 'from-emerald-500 to-green-500', features: ['Transformation locale', 'Circuits courts', 'Traçabilité'] },
    { icon: TreePine, title: 'HORTI\'SCOP', subtitle: 'Horticulture', desc: 'Production végétale, pépinières et solutions d\'aménagement paysager pour les territoires.', gradient: 'from-teal-500 to-emerald-500', features: ['Pépinières', 'Aménagement', 'Production'] },
    { icon: Flower2, title: 'FLORAL\'SCOP', subtitle: 'Fleuristerie', desc: 'Compositions florales, événementiel et décoration végétale avec des fleurs locales.', gradient: 'from-rose-500 to-pink-500', features: ['Créations florales', 'Événementiel', 'Décoration'] },
  ];

  const engagements = [
    { icon: Globe, title: 'Souveraineté alimentaire', desc: 'Favoriser la production locale et réduire la dépendance aux importations.' },
    { icon: Leaf, title: 'Agriculture durable', desc: 'Promouvoir des pratiques respectueuses de l\'environnement.' },
    { icon: Network, title: 'Circuits courts', desc: 'Rapprocher producteurs et consommateurs pour une économie locale forte.' },
    { icon: Shield, title: 'Qualité & Traçabilité', desc: 'Garantir l\'origine et la qualité des produits transformés.' },
  ];

  const products = [
    'Fruits tropicaux', 'Légumes locaux', 'Plantes aromatiques', 'Épices',
    'Produits transformés', 'Jus & Confitures', 'Plants & Semences', 'Fleurs coupées'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1765052293482-654d98e11e91?w=1920&q=80" 
            alt="Tropical agriculture" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-rose-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-300 border border-emerald-500/30 mb-6 px-4 py-2">
              <Leaf className="w-4 h-4 mr-2" /> Filières agricoles et végétales
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-green-100 to-white bg-clip-text text-transparent">AGRO &</span>
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Végétal</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Accompagnement des <span className="text-emerald-400 font-semibold">filières agricoles et agro-alimentaires</span> locales 
              pour une souveraineté alimentaire des Outre-mer.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section ref={branchesRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${branchesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-4 px-4 py-2"><Sprout className="w-4 h-4 mr-2" /> Nos branches</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">3 branches pour le <span className="text-emerald-400">végétal</span></h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {branches.map((branch, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${branchesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${branch.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <branch.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{branch.title}</h3>
                <p className="text-sm text-emerald-400 mb-4">{branch.subtitle}</p>
                <p className="text-slate-400 leading-relaxed mb-6">{branch.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {branch.features.map((feature, i) => (
                    <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">{feature}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Nos filières</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {products.map((product, index) => (
              <span key={index} className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-xl text-slate-300 px-5 py-3 rounded-xl border border-slate-700/50 hover:border-emerald-500/50 hover:text-emerald-300 transition-all cursor-default">
                <Leaf className="w-4 h-4" /> {product}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Engagements Section */}
      <section ref={engagementsRef} className="py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Nos <span className="text-emerald-200">engagements</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagements.map((item, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${engagementsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un projet dans le <span className="text-emerald-400">végétal</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Contactez-nous pour étudier votre projet et vous accompagner.</p>
          <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="AGROSCOP" />
      <Footer />
    </div>
  );
};

export default AgroVegetalPage;
